import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, ChatCircle, CalendarBlank, BookOpen, Books, Lightning, ChatCenteredDots } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const KIND_META = {
  chats: { icon: ChatCircle, label: "Chats" },
  posts: { icon: CalendarBlank, label: "Planner" },
  povs: { icon: Lightning, label: "POV & Ideas" },
  comments: { icon: ChatCenteredDots, label: "Comments" },
  kb: { icon: BookOpen, label: "Knowledge Base" },
  stories: { icon: Books, label: "Storybank" },
};

function highlight(text, q) {
  if (!text || !q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "var(--accent-tint)", color: "var(--accent-2)", padding: "0 2px", borderRadius: 4 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Cmd/Ctrl+K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 30);
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(trimmed)}`);
        setResults(r.data);
      } catch {}
      finally { setBusy(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const close = () => { setOpen(false); setQ(""); setResults(null); };

  const goto = (kind, item) => {
    close();
    if (kind === "chats") {
      localStorage.setItem("bl_draft_session", item.session_id);
      nav("/app/draft");
    } else if (kind === "posts") {
      nav("/app/planner");
    } else if (kind === "kb") {
      nav("/app/kb");
    } else if (kind === "stories") {
      nav("/app/storybank");
    } else if (kind === "povs") {
      nav("/app/pov");
    } else if (kind === "comments") {
      nav("/app/comments");
    }
  };

  return (
    <>
      <button
        data-testid="global-search-open"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 30); }}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-full text-sm"
        style={{ background: "var(--bg-soft)", color: "var(--ink-3)", border: "1px solid var(--line-2)" }}
        title="Search everywhere (Cmd+K)"
      >
        <MagnifyingGlass size={14} />
        <span className="flex-1 text-left">Search…</span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: "var(--bg-elev)", color: "var(--ink-4)" }}>⌘K</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24 overflow-y-auto"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={close}
          data-testid="global-search-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-3xl fade-up"
            style={{ background: "var(--bg-elev)", boxShadow: "var(--sh-lift)", border: "1px solid var(--line-2)" }}
          >
            <div className="flex items-center gap-3 p-5">
              <MagnifyingGlass size={18} style={{ color: "var(--ink-3)" }} />
              <input
                ref={inputRef}
                data-testid="global-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                placeholder="Search drafts, chats, knowledge base, and storybank…"
                className="flex-1 bg-transparent text-lg outline-none"
                style={{ color: "var(--ink)" }}
              />
              <button
                onClick={close}
                data-testid="global-search-close"
                className="p-2 rounded-full hover:bg-[color:var(--paper)]"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="px-2 pb-3 max-h-[65vh] overflow-y-auto scroll-thin">
              {q.trim().length < 2 && (
                <div className="p-8 text-center text-sm" style={{ color: "var(--ink-3)" }}>
                  Type at least 2 characters. Search runs across all your chats, drafts, KB sources, and stories.
                </div>
              )}

              {q.trim().length >= 2 && busy && !results && (
                <div className="p-8 text-center text-sm" style={{ color: "var(--ink-3)" }}>Searching…</div>
              )}

              {results && Object.entries({
                chats: results.chats,
                posts: results.posts,
                povs: results.povs,
                comments: results.comments,
                kb: results.kb,
                stories: results.stories,
              }).map(([kind, items]) => {
                if (!items || items.length === 0) return null;
                const Meta = KIND_META[kind];
                const Icon = Meta.icon;
                return (
                  <div key={kind} className="mb-2" data-testid={`search-section-${kind}`}>
                    <div className="px-4 py-2 flex items-center gap-2">
                      <Icon size={13} style={{ color: "var(--ink-3)" }} />
                      <div className="eyebrow">{Meta.label}</div>
                      <span className="text-xs font-mono" style={{ color: "var(--ink-4)" }}>{items.length}</span>
                    </div>
                    {items.slice(0, 6).map((it, i) => {
                      let title = "";
                      let snippet = "";
                      if (kind === "chats") {
                        title = it.role === "user" ? "You wrote" : "She replied";
                        snippet = it.snippet;
                      } else if (kind === "posts") {
                        title = it.topic || "Untitled draft";
                        snippet = (it.content || "").slice(0, 220);
                      } else if (kind === "kb") {
                        title = it.title || "Untitled source";
                        snippet = it.summary || "";
                      } else if (kind === "stories") {
                        title = it.title || "Untitled story";
                        snippet = it.preview || "";
                      } else if (kind === "povs") {
                        title = (it.content || "").slice(0, 90);
                        snippet = it.source ? `from ${it.source}` : "";
                      } else if (kind === "comments") {
                        title = it.author_name ? `on ${it.author_name}'s post` : "Your comment";
                        snippet = it.comment_text || "";
                      }
                      return (
                        <button
                          key={(it.id || it.session_id) + i}
                          data-testid={`search-hit-${kind}-${i}`}
                          onClick={() => goto(kind, it)}
                          className="w-full text-left px-4 py-3 rounded-2xl mb-1 hover:bg-[color:var(--bg-soft)] transition-colors"
                        >
                          <div className="text-sm font-medium mb-0.5 truncate">{highlight(title, q)}</div>
                          <div className="text-xs line-clamp-2" style={{ color: "var(--ink-2)" }}>{highlight(snippet, q)}</div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {results && !results.chats?.length && !results.posts?.length && !results.kb?.length && !results.stories?.length && !results.povs?.length && !results.comments?.length && (
                <div className="p-8 text-center text-sm" style={{ color: "var(--ink-3)" }} data-testid="search-empty">
                  No results for "{q}".
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
